import { useCallback, useEffect, useState } from 'react'
import { ClipboardCheck } from 'lucide-react'
import { cmsApi } from '../lib/api'
import { formatDateTime } from '../lib/format'
import type { AuditItem, CmsUser, PopularSection } from '../types'
import { LoadingState } from './LoadingState'

const actionLabel: Record<string, string> = {
  create: 'Criou um evento', update: 'Atualizou um evento', published: 'Republicou um evento',
  canceled: 'Cancelou um evento', deleted: 'Excluiu um evento', cms_user_upsert: 'Alterou um acesso',
  place_amenities: 'Conferiu um lugar', place_create: 'Criou um lugar', place_update: 'Editou um lugar',
  place_delete: 'Excluiu um lugar', producer_create: 'Criou um perfil', producer_update: 'Editou um perfil',
  producer_delete: 'Excluiu um perfil', popular_picks: 'Mudou o Popular',
  merge: 'Juntou eventos duplicados', run_credits: 'Mudou os perfis de um evento',
  source_place_create: 'Adicionou uma fonte', source_place_update: 'Editou uma fonte',
  follow_source: 'Voltou a seguir a coleta', place_faqs: 'Mudou as perguntas de um lugar',
  save_taxonomy: 'Alterou categorias e temas', reorder_taxonomy: 'Mudou a ordem de categorias ou temas',
}

const POPULAR_SECTIONS: Record<PopularSection, string> = { top: 'Top 3', film: 'Vale o ingresso', profiles: 'Para você seguir' }

// What an entry touched: the event, place or profile by name; a Popular
// section with its city.
const subjectOf = (item: AuditItem) => item.entity_type === 'popular' && item.section
  ? `${POPULAR_SECTIONS[item.section] ?? item.section}${item.city ? ` · ${item.city}` : ''}`
  : item.title

// Every action in the CMS, kept for good (PRD.md, item 27): who, when and on
// what, by person and period, as Shopify's and Stripe's activity logs. An
// editor sees only their own, which cms-events enforces (item 45).
export function AuditPage({ user, initialActor, onOpenEvent }: { user: CmsUser; initialActor?: string; onOpenEvent: (id: string) => void }) {
  const everyone = user.role === 'owner'
  const [items, setItems] = useState<AuditItem[]>([])
  const [next, setNext] = useState<string | null>(null)
  const [users, setUsers] = useState<CmsUser[]>([])
  // A person opened from the Visão geral's team.
  const [actor, setActor] = useState(everyone ? initialActor ?? '' : '')
  const [from, setFrom] = useState('')
  const [to, setTo] = useState('')
  const [loading, setLoading] = useState(true)
  const [more, setMore] = useState(false)
  const [error, setError] = useState('')

  useEffect(() => { if (everyone) cmsApi.listUsers().then(setUsers).catch(() => setUsers([])) }, [everyone])

  const page = useCallback(async (before: string | null) => {
    const params = new URLSearchParams()
    if (actor) params.set('actor', actor)
    if (from) params.set('from', from)
    if (to) params.set('to', to)
    if (before) params.set('before', before)
    return cmsApi.listAudit(params)
  }, [actor, from, to])

  useEffect(() => {
    let active = true
    setLoading(true)
    setError('')
    page(null)
      .then((result) => { if (active) { setItems(result.items); setNext(result.next) } })
      .catch((requestError) => { if (active) setError(requestError instanceof Error ? requestError.message : 'Não foi possível carregar o histórico.') })
      .finally(() => { if (active) setLoading(false) })
    return () => { active = false }
  }, [page])

  const loadMore = async () => {
    setMore(true)
    try {
      const result = await page(next)
      setItems((current) => [...current, ...result.items])
      setNext(result.next)
    } catch (requestError) {
      setError(requestError instanceof Error ? requestError.message : 'Não foi possível carregar o histórico.')
    } finally {
      setMore(false)
    }
  }

  return (
    <div className="page-stack">
      <section className="page-heading"><h1>Histórico</h1><p>{everyone
        ? 'Tudo o que a equipe fez no CMS, guardado para sempre: quem, quando e em quê.'
        : 'Tudo o que você fez no CMS, guardado para sempre: quando e em quê.'}</p></section>
      <section className="panel panel--flush">
        <div className="filter-bar">
          {everyone && <select aria-label="Pessoa" value={actor} onChange={(event) => setActor(event.target.value)}>
            <option value="">Todas as pessoas</option>
            {users.map((member) => <option key={member.id} value={member.email}>{member.email}</option>)}
          </select>}
          <label className="filter-date"><span>De</span><input type="date" aria-label="De" value={from} max={to || undefined} onChange={(event) => setFrom(event.target.value)} /></label>
          <label className="filter-date"><span>Até</span><input type="date" aria-label="Até" value={to} min={from || undefined} onChange={(event) => setTo(event.target.value)} /></label>
        </div>
        {loading ? <LoadingState /> : error ? <div className="error-state"><p>{error}</p></div> : items.length ? (
          <div className="audit-list">
            {items.map((item) => {
              const subject = subjectOf(item)
              return (
                <article key={item.id}>
                  <div>
                    <strong>{actionLabel[item.action] ?? item.action}</strong>
                    <p>
                      <b>{item.actor_email}</b>
                      {subject && <> · {item.event_id
                        ? <button type="button" className="text-button audit-subject" onClick={() => onOpenEvent(item.event_id!)}>{subject}</button>
                        : subject}</>}
                    </p>
                  </div>
                  <time dateTime={item.created_at}>{formatDateTime(item.created_at)}</time>
                </article>
              )
            })}
          </div>
        ) : <div className="empty-state"><ClipboardCheck size={30} /><h3>Nenhuma ação registrada</h3><p>Revise a pessoa ou o período.</p></div>}
        {next && !loading && (
          <div className="audit-more"><button type="button" className="button button--secondary" disabled={more} onClick={() => void loadMore()}>{more ? 'Carregando…' : 'Carregar mais'}</button></div>
        )}
      </section>
    </div>
  )
}
